// @flow

import * as React from 'react';
import get from 'lodash/get';
import PinTypeIdentifier from 'app/common/react/components/DeprecatedPinRep/PinTypeIdentifier/PinTypeIdentifier';
import type { BuyableProduct, RichSummary } from 'app/packages/pinterest-flow-types/pin';
import { Box, Text } from 'gestalt';

/**
 * Mapping from API returned availability enum fields to constants
 * @type {number}
 */
const [IN_STOCK, OUT_OF_STOCK, PREORDER, UNAVAILABLE] = [1, 2, 3, 4];

/**
 * Mapping from constants to text for display
 * @type {Object}
 */
const AVAILABILITY_OPTIONS = {
  [IN_STOCK]: 'In stock',
  [OUT_OF_STOCK]: 'Out of stock',
  [PREORDER]: 'Preorder',
  [UNAVAILABLE]: 'Unavailable',
};

type Props = {
  buyableProduct?: BuyableProduct,
  hideAvailability?: boolean,
  merchantName?: string,
  // The Pin description should already come in truncated if desired
  pinDescription: string,
  // The Pin title should already come in selected based on the
  // Pin type and truncated if desired
  pinTitle?: string,
  richSummary?: RichSummary,
  showDescription?: boolean,
  showPrice?: boolean,
  showProductIcon?: boolean,
};

export default function ProductPinMetaWrapper(props: Props) {
  const {
    buyableProduct,
    hideAvailability,
    merchantName,
    pinDescription,
    pinTitle,
    richSummary,
    showProductIcon,
    showPrice,
    showDescription,
  } = props;

  // Figure out the price information from the buyable product then rich summary
  const {
    is_available: isAvailable,
    display_active_min_price: minPrice,
    display_active_max_price: maxPrice,
  } = buyableProduct || {};
  const buyableProductPrice = minPrice === maxPrice ? minPrice : `${minPrice} - ${maxPrice}`;
  const richSummaryPrice = get(richSummary, ['products', '0', 'offer_summary', 'price']);
  const price = buyableProductPrice || richSummaryPrice;
  // Figure out the availability information from the buyable product then rich summary
  const buyableProductAvailabiltiy = isAvailable ? IN_STOCK : OUT_OF_STOCK; // Artificially map to integer values
  const richSummaryAvailability = get(richSummary, [
    'products',
    '0',
    'offer_summary',
    'availability',
  ]);
  const availability =
    buyableProduct && isAvailable != null ? buyableProductAvailabiltiy : richSummaryAvailability;
  const availabilityLabel = !hideAvailability && AVAILABILITY_OPTIONS[availability];

  return (
    <Box marginTop={1}>
      {showProductIcon && (
        <Box paddingY={1} display="flex">
          <PinTypeIdentifier text={merchantName} icon="tag" />
        </Box>
      )}
      {!!price &&
        showPrice && (
          <Box display="flex" paddingY={1}>
            <Box marginRight={2}>
              <Text size="sm">{price}</Text>
            </Box>
            {availabilityLabel &&
              availability === OUT_OF_STOCK && (
                <Text size="sm" color={'gray'}>
                  {availabilityLabel}
                </Text>
              )}
          </Box>
        )}
      {showDescription && (
        <Box paddingY={1}>
          <Text size="sm" weight="bold">
            {pinTitle || pinDescription}
          </Text>
        </Box>
      )}
    </Box>
  );
}
